import { addDays, DateOnly } from '@if/core/date'
import { t } from '@if/core/i18n'
import { EFinancingProgram, EPaymentFrequency, EProvince, getEProvinceList } from '@if/core/types'
import * as yup from '@if/core/yup'
import { getToday } from '@src/services/DateTimeProvider'
import { EProduct, ETerm, getEBankFailureCodeList, getEProductList, getETermList } from '@src/types/constants'
import type { TestLoanFundedProduct } from '@src/types/test-loan'

export const WIZARD_PROGRAMS = [EFinancingProgram.Auto, EFinancingProgram.IfXpress]
export const WIZARD_FREQUENCIES = [EPaymentFrequency.Monthly, EPaymentFrequency.BiWeekly]
export const FIRST_PAYMENT_OFFSET_DAYS = 7

export const defaultFundedProducts = (program: EFinancingProgram): TestLoanFundedProduct[] =>
  program === EFinancingProgram.IfXpress
    ? [{ productId: EProduct.CapitalFinanced, amount: 5000 }]
    : [
        { productId: EProduct.CapitalFinanced, amount: 9500 },
        { productId: EProduct.FinanceFee, amount: 600 },
        { productId: EProduct.CreditInsurance, amount: 2000 },
        { productId: EProduct.MechanicalWarranty, amount: 3500 },
      ]

export const firstPaymentFor = (activation: DateOnly) => new DateOnly(addDays(activation, FIRST_PAYMENT_OFFSET_DAYS))

export const TestLoanSchema = yup.object().shape({
  financingProgramId: yup.mixed<EFinancingProgram>().required().oneOf(WIZARD_PROGRAMS).default(EFinancingProgram.Auto),
  firstName: yup.text().required().max(50).default(''),
  lastName: yup.text().required().max(50).default(''),
  fundedProducts: yup
    .array()
    .of(
      yup.object().shape({
        productId: yup.numberEnum(getEProductList()).required(),
        amount: yup.number().required().moreThan(0),
      }),
    )
    .required()
    .min(1)
    .default(() => defaultFundedProducts(EFinancingProgram.Auto)),
  interestRate: yup.percent().required().moreThan(0).lessThan(1).default(0.1999),
  frequency: yup.mixed<EPaymentFrequency>().required().oneOf(WIZARD_FREQUENCIES).default(EPaymentFrequency.Monthly),
  term: yup.numberEnum(getETermList()).required().default(ETerm.Sixty),
  stateIso: yup.mixed<EProvince>().required().oneOf(getEProvinceList()).default(EProvince.Quebec),
  // Lazy defaults: read the day when the form is instantiated, not when the module loads.
  activationPlannedOn: yup.dateOnly().required().default(() => getToday()),
  // Field-level (not object-level) so FormStepper's trigger on step 1 surfaces it on the field.
  firstPaymentExpectedOn: yup
    .dateOnly()
    .required()
    .default(() => firstPaymentFor(getToday()))
    .test(
      'firstPaymentExpectedOn-Invalid',
      () => t('loan.error.firstPaymentEarlierThanActivationPlannedOn'),
      (value, context) => {
        const activation: DateOnly | null | undefined = (context.parent as { activationPlannedOn?: DateOnly | null })
          .activationPlannedOn
        return !value || !activation || activation < value
      },
    ),
  failedPayments: yup
    .array()
    .of(
      yup.object().shape({
        effectiveDate: yup.dateOnly().required(),
        bankFailureCode: yup.numberEnum(getEBankFailureCodeList()).required(),
      }),
    )
    .required()
    .default([]),
})

export type TestLoanSchemaType = yup.InferSubmit<typeof TestLoanSchema>
export type TestLoanDraftType = yup.InferDraft<typeof TestLoanSchema>
